import { supabase } from '../lib/supabase';

class LoyaltyService {
  // Get user's wallet information
  async getUserWallet(userId) {
    try {
      const { data, error } = await supabase?.rpc('get_wallet_summary', { user_uuid: userId });
      
      if (error) {
        return { data: null, error };
      }
      
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Failed to fetch wallet information. Please try again.' } 
      };
    }
  }

  // Get user's transaction history
  async getTransactionHistory(userId, limit = 20, offset = 0) {
    try {
      const { data, error } = await supabase?.from('loyalty_transactions')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);
      
      if (error) {
        return { data: [], error };
      }
      
      return { data: data || [], error: null };
    } catch (error) {
      return { 
        data: [], 
        error: { message: 'Failed to fetch transaction history. Please try again.' } 
      };
    }
  }

  // Get available rewards from catalog
  async getRewardsCatalog(category = null, limit = 20, offset = 0) {
    try {
      let query = supabase?.from('rewards_catalog')?.select('*')?.eq('status', 'active')?.order('created_at', { ascending: false });
      
      if (category) {
        query = query?.eq('category', category);
      }
      
      query = query?.range(offset, offset + limit - 1);
      
      const { data, error } = await query;
      
      if (error) {
        return { data: [], error };
      }
      
      return { data: data || [], error: null };
    } catch (error) {
      return { 
        data: [], 
        error: { message: 'Failed to fetch rewards catalog. Please try again.' } 
      };
    }
  }

  // Redeem a reward
  async redeemReward(userId, rewardId, quantity = 1) {
    try {
      // First, get the reward details and user's wallet
      const { data: reward, error: rewardError } = await supabase?.from('rewards_catalog')?.select('*')?.eq('id', rewardId)?.eq('status', 'active')?.single();
      
      if (rewardError) {
        return { data: null, error: rewardError };
      }
      
      if (!reward) {
        return { data: null, error: { message: 'Reward not found or unavailable' } };
      }
      
      const { data: wallet, error: walletError } = await supabase?.from('user_wallets')?.select('*')?.eq('user_id', userId)?.single();
      
      if (walletError) {
        return { data: null, error: walletError };
      }
      
      const totalCost = reward?.token_cost * quantity;
      
      if (wallet?.token_balance < totalCost) {
        return { 
          data: null, 
          error: { message: 'Insufficient tokens for this redemption' } 
        };
      }
      
      // Check stock availability
      if (reward?.stock_quantity !== null && reward?.stock_quantity < quantity) {
        return { 
          data: null, 
          error: { message: 'Insufficient stock available' } 
        };
      }
      
      // Start transaction by creating the spending transaction first
      const { data: transaction, error: transactionError } = await supabase?.from('loyalty_transactions')?.insert({
          user_id: userId,
          wallet_id: wallet?.id,
          transaction_type: 'spent_reward',
          amount: -totalCost,
          description: `Redeemed ${quantity}x ${reward?.title}`,
          reference_id: rewardId,
          reference_table: 'rewards_catalog',
          metadata: {
            reward_title: reward?.title,
            quantity: quantity,
            cost_per_item: reward?.token_cost
          }
        })?.select()?.single();
      
      if (transactionError) {
        return { data: null, error: transactionError };
      }
      
      // Create the redemption record
      const { data: redemption, error: redemptionError } = await supabase?.from('reward_redemptions')?.insert({
          user_id: userId,
          reward_id: rewardId,
          transaction_id: transaction?.id,
          quantity: quantity,
          total_tokens: totalCost,
          status: 'pending',
          redemption_details: {
            redeemed_at: new Date()?.toISOString(),
            reward_title: reward?.title,
            reward_category: reward?.category
          }
        })?.select()?.single();
      
      if (redemptionError) {
        return { data: null, error: redemptionError };
      }
      
      // Update stock if applicable
      if (reward?.stock_quantity !== null) {
        await supabase?.from('rewards_catalog')?.update({ stock_quantity: reward?.stock_quantity - quantity })?.eq('id', rewardId);
      }
      
      return { data: redemption, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Failed to redeem reward. Please try again.' } 
      };
    }
  }

  // Award tokens for activities
  async awardTokensForActivity(userId, activityId, activityType, duration = null) {
    try {
      // Get earning rule for activity completion
      const { data: rule, error: ruleError } = await supabase?.from('token_earning_rules')?.select('*')?.eq('trigger_type', 'activity_complete')?.eq('is_active', true)?.single();
      
      if (ruleError || !rule) {
        return { data: null, error: { message: 'No earning rule found for activities' } };
      }
      
      // Get user's wallet
      const { data: wallet, error: walletError } = await supabase?.from('user_wallets')?.select('*')?.eq('user_id', userId)?.single();
      
      if (walletError) {
        return { data: null, error: walletError };
      }
      
      // Check daily limits
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { data: todayTransactions } = await supabase?.from('loyalty_transactions')?.select('amount')?.eq('user_id', userId)?.eq('transaction_type', 'earned_activity')?.gte('created_at', `${today}T00:00:00Z`)?.lt('created_at', `${today}T23:59:59Z`);
      
      const todayTotal = todayTransactions?.reduce((sum, t) => sum + t?.amount, 0) || 0;
      
      if (rule?.max_per_day && todayTotal >= rule?.max_per_day) {
        return { 
          data: null, 
          error: { message: 'Daily token earning limit reached' } 
        };
      }
      
      let tokensToAward = rule?.tokens_per_trigger;
      
      // Apply any bonus conditions
      if (rule?.conditions?.min_duration_minutes && duration >= rule?.conditions?.min_duration_minutes) {
        tokensToAward = Math.floor(tokensToAward * (rule?.conditions?.bonus_multiplier || 1));
      }
      
      // Don't exceed daily limit
      if (rule?.max_per_day && (todayTotal + tokensToAward) > rule?.max_per_day) {
        tokensToAward = rule?.max_per_day - todayTotal;
      }
      
      if (tokensToAward <= 0) {
        return { 
          data: null, 
          error: { message: 'No tokens to award' } 
        };
      }
      
      // Create the earning transaction
      const { data: transaction, error: transactionError } = await supabase?.from('loyalty_transactions')?.insert({
          user_id: userId,
          wallet_id: wallet?.id,
          transaction_type: 'earned_activity',
          amount: tokensToAward,
          description: `Earned tokens for completing ${activityType} activity`,
          reference_id: activityId,
          reference_table: 'activities',
          metadata: {
            activity_type: activityType,
            duration_minutes: duration,
            rule_applied: rule?.rule_name
          }
        })?.select()?.single();
      
      if (transactionError) {
        return { data: null, error: transactionError };
      }
      
      return { data: transaction, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Failed to award tokens. Please try again.' } 
      };
    }
  }

  // Award tokens for goal achievement
  async awardTokensForGoal(userId, goalId, goalType) {
    try {
      // Get earning rule for goal achievement
      const { data: rule, error: ruleError } = await supabase?.from('token_earning_rules')?.select('*')?.eq('trigger_type', 'goal_achieved')?.eq('is_active', true)?.single();
      
      if (ruleError || !rule) {
        return { data: null, error: { message: 'No earning rule found for goals' } };
      }
      
      // Get user's wallet
      const { data: wallet, error: walletError } = await supabase?.from('user_wallets')?.select('*')?.eq('user_id', userId)?.single();
      
      if (walletError) {
        return { data: null, error: walletError };
      }
      
      let tokensToAward = rule?.tokens_per_trigger;
      
      // Apply bonus multiplier if specified
      if (rule?.conditions?.bonus_multiplier) {
        tokensToAward = Math.floor(tokensToAward * rule?.conditions?.bonus_multiplier);
      }
      
      // Create the earning transaction
      const { data: transaction, error: transactionError } = await supabase?.from('loyalty_transactions')?.insert({
          user_id: userId,
          wallet_id: wallet?.id,
          transaction_type: 'earned_goal',
          amount: tokensToAward,
          description: `Achieved ${goalType} goal`,
          reference_id: goalId,
          reference_table: 'health_goals',
          metadata: {
            goal_type: goalType,
            rule_applied: rule?.rule_name
          }
        })?.select()?.single();
      
      if (transactionError) {
        return { data: null, error: transactionError };
      }
      
      return { data: transaction, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Failed to award tokens for goal. Please try again.' } 
      };
    }
  }

  // Get user's redemption history
  async getRedemptionHistory(userId, limit = 10, offset = 0) {
    try {
      const { data, error } = await supabase?.from('reward_redemptions')?.select(`
          *,
          reward:rewards_catalog(title, description, category, image_url),
          transaction:loyalty_transactions(amount, created_at)
        `)?.eq('user_id', userId)?.order('redeemed_at', { ascending: false })?.range(offset, offset + limit - 1);
      
      if (error) {
        return { data: [], error };
      }
      
      return { data: data || [], error: null };
    } catch (error) {
      return { 
        data: [], 
        error: { message: 'Failed to fetch redemption history. Please try again.' } 
      };
    }
  }

  // Get token earning rules
  async getEarningRules() {
    try {
      const { data, error } = await supabase?.from('token_earning_rules')?.select('*')?.eq('is_active', true)?.order('tokens_per_trigger', { ascending: false });
      
      if (error) {
        return { data: [], error };
      }
      
      return { data: data || [], error: null };
    } catch (error) {
      return { 
        data: [], 
        error: { message: 'Failed to fetch earning rules. Please try again.' } 
      };
    }
  }

  // Format tokens for display
  formatTokens(tokens) {
    if (tokens == null) return '0';
    return tokens?.toLocaleString();
  }

  // Get reward categories
  getRewardCategories() {
    return [
      { value: 'wellness_products', label: 'Wellness Products' },
      { value: 'marketplace_discount', label: 'Marketplace Discounts' },
      { value: 'workshop_access', label: 'Workshop Access' },
      { value: 'consultation_time', label: 'Expert Consultations' },
      { value: 'premium_content', label: 'Premium Content' },
      { value: 'merchandise', label: 'Merchandise' },
      { value: 'gift_card', label: 'Gift Cards' }
    ];
  }

  // Get transaction type labels
  getTransactionTypeLabel(type) {
    const labels = {
      'earned_activity': 'Activity Completed',
      'earned_goal': 'Goal Achieved',
      'earned_streak': 'Streak Milestone',
      'earned_challenge': 'Challenge Completed',
      'earned_referral': 'Referral Bonus',
      'spent_reward': 'Reward Redeemed',
      'spent_marketplace': 'Marketplace Purchase',
      'bonus': 'Bonus Award',
      'penalty': 'Penalty',
      'refund': 'Refund',
      'admin_adjustment': 'Admin Adjustment'
    };
    return labels?.[type] || type;
  }
}

export const loyaltyService = new LoyaltyService();
export default loyaltyService;